(() => {
  if (window.WNHeaderNavigation) return;
  window.WNHeaderNavigation = true;

  class WnMenuDrawer extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.details = this.querySelector('details');
      this.panel = this.querySelector('[data-menu-panel]');
      this.summary = this.details?.querySelector(':scope > summary');
      if (!this.details || !this.panel || !this.summary) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.details.addEventListener('toggle', () => this.onToggle(), { signal });
      this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.close(), { signal });
      this.querySelector('[data-menu-overlay]')?.addEventListener('click', () => this.close(), { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    focusableElements() {
      return [...this.panel.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
    }

    onToggle() {
      if (this.details.open) requestAnimationFrame(() => this.querySelector('[data-menu-close]')?.focus({ preventScroll: true }));
    }

    onKeydown(event) {
      if (!this.details.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    close() {
      if (!this.details.open) return;
      this.details.open = false;
      this.summary.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('wn-menu-drawer')) customElements.define('wn-menu-drawer', WnMenuDrawer);

  function mountDisclosures(scope = document) {
    scope.querySelectorAll?.('[data-header-disclosure]').forEach((details) => {
      if (details.dataset.headerDisclosureReady === 'true') return;
      details.dataset.headerDisclosureReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('[data-header-disclosure][open]').forEach((other) => { if (other !== details) other.open = false; });
      });
    });
  }

  let headerObserver = null;
  function observeHeaderHeight() {
    const group = document.querySelector('.shopify-section-group-header-group');
    headerObserver?.disconnect();
    if (!group) return;
    const update = () => document.documentElement.style.setProperty('--wn-header-group-height', `${Math.ceil(group.getBoundingClientRect().height)}px`);
    update();
    headerObserver = new ResizeObserver(update);
    headerObserver.observe(group);
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-header-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = [...document.querySelectorAll('[data-header-disclosure][open]')].pop();
    if (!open) return;
    open.open = false;
    open.querySelector(':scope > summary')?.focus({ preventScroll: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    mountDisclosures(event.target);
    observeHeaderHeight();
  });

  let lastSearchTrigger = null;

  function getSearchOverlay() {
    return document.querySelector('[data-wn-search-overlay]');
  }

  function openSearch(trigger) {
    const overlay = getSearchOverlay();
    if (!overlay) return;
    lastSearchTrigger = trigger || document.activeElement;
    document.querySelectorAll('[data-header-disclosure][open]').forEach((details) => { details.open = false; });
    overlay.hidden = false;
    document.body.classList.add('wn-search-open');
    requestAnimationFrame(() => overlay.querySelector('[data-search-input]')?.focus({ preventScroll: true }));
  }

  function closeSearch() {
    const overlay = getSearchOverlay();
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    document.body.classList.remove('wn-search-open');
    lastSearchTrigger?.focus?.({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest?.('[data-search-open]');
    if (opener) {
      event.preventDefault();
      openSearch(opener);
      return;
    }
    if (event.target.closest?.('[data-search-close]')) {
      event.preventDefault();
      closeSearch();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !getSearchOverlay()?.hidden) {
      event.preventDefault();
      closeSearch();
    }
  });

  mountDisclosures();
  observeHeaderHeight();
})();
